import { ErrorMessage } from "@atlaskit/form";
import { currencyFormatter } from "@fla/common";
import {
  Button,
  ConsentOrdersPathName,
  DebtBaseProps,
  DebtBaseSchema,
  DebtType,
  Debt_InitialState,
  FormNavigation,
  RadioSelect,
  useDebts,
  useNames
} from "@fla/components";
import DebtIcon from "@fla/components/src/assets/svgs/debts.svg";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "app/store";
import { withConsentOrder } from "components/HOCs/withConsentOrder";
import { useConsentOrders } from "hooks/ConsentOrders";
import { SliceStatus } from "interfaces";
import { selectBaseAssetsDebts, selectConsentOrdersBase, selectDebt } from "pages/ConsentOrders";
import { consentOrdersActions } from "pages/ConsentOrders/consentOrdersSlice";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

const defaultValues = {
  if_own_debts: ""
};

function Debts() {
  const { initialDetails } = useSelector(selectConsentOrdersBase);
  const debtsData = useSelector(selectDebt(DebtType.DEBTS)) as Debt_InitialState[];
  const _useNames = useNames({ initialDetails });
  const { spouseFirstName } = _useNames;
  const assetsDebts = useSelector(selectBaseAssetsDebts);
  const [status, setStatus] = useState(SliceStatus.Idle);
  const [error, setError] = useState<string>();

  const location = useLocation();
  const navigate = useNavigate();
  const { control, handleSubmit, watch, reset, getValues } = useForm({
    mode: "all",
    defaultValues,
    resolver: yupResolver(DebtBaseSchema)
  });

  const handleBackClick = () => {
    navigate(ConsentOrdersPathName.Furniture);
  };

  const editData = (values?: DebtBaseProps) => {
    navigate(`${ConsentOrdersPathName.Debts}/add`, {
      state: {
        parent: getValues(),
        child: values
      }
    });
  };

  const dispatch = useAppDispatch();
  const { updateAssetsDebts } = useConsentOrders();

  const onSubmit = async (values: DebtBaseProps) => {
    try {
      setStatus(SliceStatus.Pending);

      if (values.if_own_debts === "no" && debtsData.length) {
        setError("If neither of you have any debts in Australia, then please remove the added debts details.");
      } else if (values.if_own_debts === "yes" && !debtsData.length) {
        setError("please add at least one debt.");
      } else {
        setError("");
        const payload = {
          debts: { data: values, isCompleted: true },
          currentStep: 10
        };
        await updateAssetsDebts(payload);
        dispatch(consentOrdersActions.setAssetsDebts(payload));
        navigate(ConsentOrdersPathName.AdditionalRequirements);
      }
      setStatus(SliceStatus.Resolved);
    } catch (error) {
      setStatus(SliceStatus.Rejected);
    }
  };

  useEffect(() => {
    if (location.state) {
      reset(location.state);
    } else {
      reset(assetsDebts?.debts?.data);
    }
  }, [location]);

  const { ownCard, splitCard, userTotalAmount, spouseTotalAmount } = useDebts({
    debtsData,
    ..._useNames
  });

  return (
    <section className="w-full py-10">
      <form
        id="bank-form"
        className="md:w-10/12 lg:max-w-2xl mx-auto h-fit px-5 md:px-10 md:py-5 space-y-5"
        onSubmit={handleSubmit(onSubmit)}
      >
        <img src={DebtIcon} className="w-10 h-auto mx-auto my-3" alt="debts" />

        <h3 className="text-center  my-5">Debts</h3>
        <RadioSelect
          title={`Do you or ${spouseFirstName} own an have any other debts?`}
          subTitle="Eg HECS debt, ATO debt, credit card, overseas debt, hire purchase, pool loans."
          name="if_own_debts"
          id="if_own_debts"
          control={control}
          options={[
            { label: "Yes", value: "yes" },
            { label: "No", value: "no" }
          ]}
          type="card"
          classes="mt-12"
          onChange={() => setError("")}
          theme="dark"
        />

        <div className="w-full py-10 border border-gray-400 border-dashed rounded-lg flex flex-col items-center justify-center space-y-10 mb-5">
          <p className="px-16 text-center text-base font-bold">
            Please add all the all the other debts both you and&nbsp;
            {spouseFirstName} have in Australia
          </p>
          <Button
            className="px-10 py-2 uppercase"
            fontSize="sm"
            onClick={() => editData(undefined)}
            disabled={watch("if_own_debts") !== "yes"}
          >
            + Add Debts
          </Button>
        </div>
        {error ? (
          <div className="max-w-sm mx-auto text-center">
            <ErrorMessage>{error}</ErrorMessage>
          </div>
        ) : null}

        {Array.isArray(debtsData) && debtsData.length ? (
          <>
            <p className="uppercase text-gray-500 text-xl font-medium my-5">You’re Responsible</p>

            {ownCard("user", editData)}
            {splitCard("user", editData)}
            <section className="flex justify-end items-center mb-12">
              <p className="text-gray-600">
                Total:
                <span className="text-2xl ml-2">{currencyFormatter.format(userTotalAmount)}</span>
              </p>
            </section>

            <p className="uppercase text-gray-500 text-xl font-medium my-5">{spouseFirstName} Responsible</p>

            {ownCard("spouse", editData)}
            {splitCard("spouse", editData)}
            <section className="flex justify-end items-center mb-12">
              <p className="text-gray-600">
                Total:
                <span className="text-2xl ml-2">{currencyFormatter.format(spouseTotalAmount)}</span>
              </p>
            </section>
          </>
        ) : null}

        <FormNavigation
          isFirst={false}
          isLast={false}
          form="bank-form"
          handleBackClick={handleBackClick}
          isSubmitting={status === SliceStatus.Pending}
          classes="mt-12"
          nextButtonText="Continue"
        />
      </form>
    </section>
  );
}
export default withConsentOrder(Debts);
